import Cookies from 'js-cookie';
import { get, post } from '../../util/request';
import { shopify_base_url, platform_base_url } from '../../util/config';
import { CURRENT_USER } from '../App.gql';
import {
  CUSTOMER_ACCESS_TOKEN,
  GET_CHECKOUT_URL,
  REFRESH_CUSTOMER_ACCESS_TOKEN
} from './Authentication.gql';
import { getCookieDomain } from '../../util/util';
import { UPDATE_CART_BUYER_IDENTITY } from '../../services/storefront/cart/mutations';

const CUSTOMER_ACCESS_TOKEN_COOKIE = 'BOM-CAT';

/**
 * Dynamically inserts Facebook SDK into page
 * @returns {Void}
 */
export const loadFacebookSDK = () => {
  if (process.env.NODE_ENV === 'test') {
    throw new Error(
      `Attempting to load Facebook SDK into testing environment.\n
      Instead, you might want to automock 'useFacebookSdk' hook.\n
      Example: jest.mock('../util/Authentication.hooks')`
    );
  }
  (function fbLoad(_d, s, id) {
    const js = document.createElement(s);
    const fjs = document.getElementsByTagName(s)[0];
    if (document.getElementById(id)) return;
    js.id = id;
    js.async = true;
    js.setAttribute('data-status', 'loading');
    js.src = 'https://connect.facebook.net/en_US/sdk.js';
    fjs.parentNode.insertBefore(js, fjs);
  })(document, 'script', 'facebook-jssdk');
};

/**
 * Dynamically inserts Google Client SDK into page
 * @returns {Void}
 */
export const loadGoogleClientSDK = () => {
  if (process.env.NODE_ENV === 'test') {
    throw new Error(
      `Attempting to load Google Client SDK into testing environment.\n
      Instead, you might want to automock 'useGoogleClientSdk' hook.\n
      Example: jest.mock('../util/Authentication.hooks')`
    );
  }
  (function googleLoad(_d, s, id) {
    const js = document.createElement(s);
    const gjs = document.getElementsByTagName(s)[0];
    if (document.getElementById(id)) return;

    js.id = id;
    js.async = true;
    js.setAttribute('data-status', 'loading');
    js.src = 'https://accounts.google.com/gsi/client';
    gjs.parentNode.insertBefore(js, gjs);
  })(document, 'script', 'google-clientsdk');
};

/**
 * Logs a user into a Shopify session from a provided multi pass token
 * @param {String} token multipass token returned from the Identity service
 */
export const createShopifySession = async (token) => {
  if (!token)
    throw new Error(
      'You must supply a multipass token to create a Shopify session'
    );

  return get(`${shopify_base_url}/account/login/multipass/${token}`)
    .then(() => {
      Cookies.set('BOM-SESSION_INIT', 1, {
        domain: getCookieDomain(),
        expires: 1
      });
    })
    .catch(() => {});
};

/**
 * Update customer's cart with multipass token
 */
export const updateCartBuyerIdentity = async (client, token) => {
  if (!token)
    throw new Error(
      'You must supply a multipass token to create a Shopify session'
    );
  const { data } = await client.query({ query: CURRENT_USER });
  const cartId = data?.cartId;
  if (cartId) {
    await client.mutate({
      context: { service: 'storefront' },
      mutation: UPDATE_CART_BUYER_IDENTITY,
      variables: {
        buyerIdentity: {
          customerAccessToken: token
        },
        cartId
      }
    });
  }
};

/**
 * Get the new checkout URL from the cart after a user is logged in
 */
export const getCartCheckoutUrlKey = async (client) => {
  const { data: userData } = await client.query({ query: CURRENT_USER });
  const cartId = userData?.cartId;
  if (!cartId) return null;
  const { data: cartData } = await client.query({
    context: { service: 'storefront' },
    query: GET_CHECKOUT_URL,
    variables: { id: cartId }
  });
  return cartData?.cart?.checkoutUrl;
};

/**
 * Writes user information to a cookie and apollo cache
 * @param {Object} client Apollo GraphQL Client Instance
 * @param {Object} account User info
 */
const cacheUser = (
  client,
  {
    first_name,
    last_name,
    email,
    email_sha256,
    bombas_id,
    last_order_date,
    orders_count,
    past_purchases,
    total_spent
  }
) => {
  const user = {
    email,
    firstName: first_name,
    lastName: last_name,
    isLoggedIn: true,
    hasShopifySession: !!Cookies.get('BOM-SESSION_INIT'),
    hashedEmail: email_sha256,
    bombasId: bombas_id,
    lastOrderDate: last_order_date,
    ordersCount: orders_count,
    hasPastPurchases: past_purchases,
    totalSpent: total_spent
  };

  Cookies.set('BOM-USER', JSON.stringify(user), {
    domain: getCookieDomain(),
    expires: 365
  });

  client.writeQuery({
    query: CURRENT_USER,
    data: {
      user
    }
  });
};

/**
 * Determines if user has an existing Shopify session while simultaneously refreshing it (when applicable).
 * @returns {Promise} Resolves `true` if a Shopify session exists, resolves `false` if one does not exist or an error occurs.
 */
export const hasShopifySession = async (client) => {
  if (!Cookies.get(CUSTOMER_ACCESS_TOKEN_COOKIE)) return false;
  if (Cookies.get('BOM-SESSION_INIT')) return true;

  return post(`${platform_base_url}/v2/identity/refresh-session`, null, {
    credentials: 'include'
  })
    .then(async ({ multipass: { token }, account }) => {
      client.mutate({
        context: { service: 'storefront' },
        mutation: REFRESH_CUSTOMER_ACCESS_TOKEN
      });

      await createShopifySession(token);
      cacheUser(client, account);
    })
    .then(() => true)
    .catch(() => false);
};

/**
 * Refreshes a Scout session but does not create a shopify session
 * @returns {Promise} Resolves `true` for a successful refresh or refresh not required, resolves `false` if an error occurs.
 */
export const refreshSession = async (client, params = {}) => {
  if (!Cookies.get(CUSTOMER_ACCESS_TOKEN_COOKIE)) return false;

  return post(
    `${platform_base_url}/v2/identity/refresh-session`,
    { return_to: params.return_to },
    {
      credentials: 'include'
    }
  )
    .then(async ({ multipass, account }) => {
      client.mutate({
        context: { service: 'storefront' },
        mutation: REFRESH_CUSTOMER_ACCESS_TOKEN
      });

      cacheUser(client, account);
      return multipass;
    })
    .then((multipass) => multipass)
    .catch((e) => {
      window.DD_RUM?.addError?.(e, { location: 'identity/refresh' });
      return false;
    });
};

/**
 * Completes an account authentication by creating a Shopify session and customer access token.
 * @param {Object} client Apollo GraphQL Client Instance
 * @param {Object} response API Response
 * @param {Object} response.multipass
 * @param {String} response.multipass.token
 * @returns {Promise} Resolves if all actions were successful; rejects if any errors occur.
 */
export const completeAuthentication = async (
  client,
  { multipass: { token }, account }
) => {
  await client
    .mutate({
      context: { service: 'storefront' },
      mutation: CUSTOMER_ACCESS_TOKEN,
      variables: { multipassToken: token }
    })
    .then(
      async ({
        data: {
          customerAccessTokenCreateWithMultipass: {
            customerAccessToken: { accessToken }
          }
        }
      }) => {
        Cookies.set(CUSTOMER_ACCESS_TOKEN_COOKIE, accessToken, {
          domain: getCookieDomain(),
          expires: 365
        });

        client.writeQuery({
          query: CURRENT_USER,
          data: {
            user: { isLoggedIn: true }
          }
        });

        await updateCartBuyerIdentity(client, accessToken);

        await createShopifySession(token);
        cacheUser(client, account);
      }
    );
  return account;
};
